import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OrgContext } from '../../../objects/Context';
import axios from 'axios';
import './SettingsComponents.css';
import './SubscriptionSettings.css';

const PLANS = [
    { id: 'free', label: 'Free', price: 0, maxClients: 0, description: 'Get started for free' },
    { id: 'early_1_5', label: '1–5 Workspaces', price: 250, maxClients: 5, description: 'For small practices' },
    { id: 'early_6_10', label: '6–10 Workspaces', price: 500, maxClients: 10, description: 'For growing practices' },
    { id: 'early_11_20', label: '11–20 Workspaces', price: 750, maxClients: 20, description: 'For established practices' },
    { id: 'early_21_50', label: '21–50 Workspaces', price: 1100, maxClients: 50, description: 'For large practices' },
    { id: 'early_51_100', label: '51–100 Workspaces', price: 1750, maxClients: 100, description: 'For enterprise practices' },
];

function SubscriptionSettings() {
    const { org, setOrg } = useContext(OrgContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);
    const [billingInfo, setBillingInfo] = useState({ periodEnd: null, invoices: [] });
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [checkoutSuccess, setCheckoutSuccess] = useState(false);

    const currentPlan = org?.subscription?.plan || 'free';
    const pendingPlan = org?.subscription?.pendingPlan || null;
    const subscriptionStatus = org?.subscription?.status;
    const isCanceling = subscriptionStatus === 'canceling';
    const isPastDue = subscriptionStatus === 'past_due';
    const hasSubscription = !!org?.subscription?.stripeCustomerId;
    const currentPlanData = PLANS.find(p => p.id === currentPlan) || PLANS[0];
    const pendingPlanData = pendingPlan ? PLANS.find(p => p.id === pendingPlan) : null;
    const currentPlanIndex = PLANS.findIndex(p => p.id === currentPlan);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('checkout') === 'success') {
            setCheckoutSuccess(true);
            navigate(location.pathname, { replace: true });
            refreshOrg();
        }
    }, []);

    useEffect(() => {
        if (hasSubscription) fetchBillingInfo();
    }, [org?.id, hasSubscription]);

    const refreshOrg = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/orgs/get-org/${org.id}`,
                { withCredentials: true }
            );
            if (response.data?.org) setOrg(response.data.org);
        } catch (err) {
            console.error('Error refreshing org:', err);
        }
    };

    const fetchBillingInfo = async () => {
        setBillingLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/billing-info?orgId=${org.id}`,
                { withCredentials: true }
            );
            setBillingInfo(response.data);
        } catch (err) {
            console.error('Error fetching billing info:', err);
        } finally {
            setBillingLoading(false);
        }
    };

    const handleSelectPlan = async (planId) => {
        if (planId === currentPlan || loading) return;

        const planLabel = PLANS.find(p => p.id === planId)?.label;
        const planIndex = PLANS.findIndex(p => p.id === planId);
        const isUpgrade = planIndex > currentPlanIndex;

        if (hasSubscription) {
            const action = isUpgrade ? 'upgrade' : 'downgrade';
            const suffix = isUpgrade
                ? 'You will be charged immediately for the difference.'
                : 'Your current plan will remain active until the end of your billing period.';
            const confirmed = window.confirm(
                `Are you sure you want to ${action} to the ${planLabel} plan? ${suffix}`
            );
            if (!confirmed) return;
        }

        const prevSubscription = org?.subscription;
        setLoading(true);

        if (hasSubscription && isUpgrade) {
            setOrg(prev => ({
                ...prev,
                subscription: { ...prev.subscription, plan: planId, pendingPlan: null, status: 'active' },
            }));
            setShowPlanModal(false);
            setCheckoutSuccess(true);
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-checkout-session`,
                { planId, orgId: org.id },
                { withCredentials: true }
            );
            if (response.data.url) {
                window.open(response.data.url, '_blank');
                setShowPlanModal(false);
            } else if (response.data.pending) {
                setOrg(prev => ({
                    ...prev,
                    subscription: { ...prev.subscription, pendingPlan: response.data.pendingPlan },
                }));
                setShowPlanModal(false);
                setCheckoutSuccess(true);
                fetchBillingInfo();
            } else if (response.data.success) {
                fetchBillingInfo();
            }
        } catch (err) {
            console.error('Error selecting plan:', err);
            setOrg(prev => ({ ...prev, subscription: prevSubscription }));
            setShowPlanModal(true);
            setCheckoutSuccess(false);
            alert('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleManagePayment = async () => {
        setPortalLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-portal-session`,
                { orgId: org.id },
                { withCredentials: true }
            );
            if (response.data.url) window.open(response.data.url, '_blank');
        } catch (err) {
            console.error('Error opening billing portal:', err);
            alert('Unable to open billing portal. Please try again.');
        } finally {
            setPortalLoading(false);
        }
    };

    const handleCancel = async () => {
        const confirmed = window.confirm(
            'Are you sure you want to cancel? Your subscription will remain active until the end of the current billing period, then revert to Free.'
        );
        if (!confirmed) return;

        const prevSubscription = org?.subscription;
        setOrg(prev => ({
            ...prev,
            subscription: { ...prev.subscription, status: 'canceling', pendingPlan: null },
        }));

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/cancel-subscription`,
                { orgId: org.id },
                { withCredentials: true }
            );
        } catch (err) {
            console.error('Error canceling subscription:', err);
            setOrg(prev => ({
                ...prev,
                subscription: prevSubscription,
            }));
            alert('Something went wrong. Please try again.');
        }
    };

    const handleReactivate = async () => {
        const prevStatus = org?.subscription?.status;
        setOrg(prev => ({
            ...prev,
            subscription: { ...prev.subscription, status: 'active' },
        }));

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/reactivate-subscription`,
                { orgId: org.id },
                { withCredentials: true }
            );
        } catch (err) {
            console.error('Error reactivating subscription:', err);
            setOrg(prev => ({
                ...prev,
                subscription: { ...prev.subscription, status: prevStatus },
            }));
            alert('Something went wrong. Please try again.');
        }
    };

    const handleCancelPendingDowngrade = async () => {
        const prevSubscription = org?.subscription;
        setOrg(prev => ({
            ...prev,
            subscription: { ...prev.subscription, pendingPlan: null },
        }));

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/stripe/cancel-pending-downgrade`,
                { orgId: org.id },
                { withCredentials: true }
            );
        } catch (err) {
            console.error('Error canceling pending downgrade:', err);
            setOrg(prev => ({ ...prev, subscription: prevSubscription }));
            alert('Something went wrong. Please try again.');
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    };

    const formatAmount = (amountCents, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: (currency || 'usd').toUpperCase(),
        }).format(amountCents / 100);
    };

    return (
        <div className="settings-component">
            <div className="settings-header">
                <div className="settings-title-group">
                    <h1 className="settings-title">Billing & Invoices</h1>
                    <h2 className="settings-subtitle">Manage your subscription, payment, and invoices</h2>
                </div>
            </div>

            {checkoutSuccess && (
                <div className="billing-success-banner" onClick={() => setCheckoutSuccess(false)}>
                    <span className="billing-success-icon">✓</span>
                    Your subscription has been updated successfully.
                </div>
            )}

            <div className="content billing-content">

                {/* ── Current Plan ── */}
                <div className="billing-section">
                    <div className="billing-section-row">
                        <div className="billing-section-info">
                            <div className="billing-plan-header">
                                <span className="billing-plan-name">{currentPlanData.label}</span>
                                {isCanceling ? (
                                    <span className="billing-status-tag canceling">Canceling</span>
                                ) : isPastDue ? (
                                    <span className="billing-status-tag past-due">Past Due</span>
                                ) : hasSubscription ? (
                                    <span className="billing-status-tag active">Active</span>
                                ) : null}
                            </div>
                            {currentPlanData.price > 0 && currentPlanData.maxClients > 0 ? (
                                <p className="billing-plan-pricing">
                                    ${currentPlanData.price.toLocaleString()} / mo (${Math.round(currentPlanData.price / currentPlanData.maxClients)} / workspace)
                                </p>
                            ) : (
                                <p className="billing-plan-pricing muted">
                                    1 Workspace (Limited)
                                </p>
                            )}
                            {pendingPlanData && billingInfo.periodEnd && (
                                <p className="billing-plan-renewal pending-info">
                                    Downgrading on {formatDate(billingInfo.periodEnd)}.{' '}
                                    <button className="billing-inline-link" onClick={handleCancelPendingDowngrade}>
                                        Keep plan
                                    </button>
                                </p>
                            )}
                            {isCanceling && billingInfo.periodEnd && (
                                <p className="billing-plan-renewal warning">
                                    Your subscription will end on {formatDate(billingInfo.periodEnd)}.
                                </p>
                            )}
                            {isPastDue && (
                                <p className="billing-plan-renewal warning">
                                    Past due. Please update your payment method.
                                </p>
                            )}
                            {hasSubscription && !isCanceling && !isPastDue && !pendingPlanData && billingInfo.periodEnd && (
                                <p className="billing-plan-renewal">
                                    Auto renews on {formatDate(billingInfo.periodEnd)}.
                                </p>
                            )}
                        </div>
                        <div className="billing-section-action">
                            {isCanceling ? (
                                <button className="billing-btn reactivate" onClick={handleReactivate} disabled={loading}>
                                    Reactivate
                                </button>
                            ) : currentPlan === 'free' || currentPlan === null ? (
                                <button className="billing-btn primary" onClick={() => setShowPlanModal(true)}>
                                    Upgrade
                                </button>
                            ) : (
                                <button className="billing-btn" onClick={() => setShowPlanModal(true)}>
                                    Adjust plan
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Payment ── */}
                {hasSubscription && (
                    <div className="billing-section">
                        <div className="billing-section-row">
                            <div className="billing-section-info">
                                <h3 className="billing-section-title">Payment</h3>
                                <p className="billing-section-desc">Update your payment details</p>
                            </div>
                            <div className="billing-section-action">
                                <button className="billing-btn" onClick={handleManagePayment} disabled={portalLoading}>
                                    {portalLoading ? 'Opening…' : 'Manage in Stripe'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Invoices ── */}
                {hasSubscription && (
                    <div className="billing-section">
                        <h3 className="billing-section-title">Invoices</h3>
                        {billingLoading ? (
                            <div className="billing-invoices-skeleton">
                                {[0, 1, 2].map(i => (
                                    <div key={i} className="billing-skeleton-row">
                                        <div className="billing-skeleton-bar" style={{ width: `${80 + i * 10}px` }} />
                                        <div className="billing-skeleton-bar" style={{ width: '48px' }} />
                                        <div className="billing-skeleton-bar" style={{ width: '64px' }} />
                                        <div className="billing-skeleton-bar" style={{ width: '36px', marginLeft: 'auto' }} />
                                    </div>
                                ))}
                            </div>
                        ) : billingInfo.invoices.length > 0 ? (
                            <table className="billing-invoices-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>Amount</th>
                                        <th>Invoice</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billingInfo.invoices.map(inv => (
                                        <tr key={inv.id}>
                                            <td>{formatDate(inv.date)}</td>
                                            <td>
                                                <span className={`billing-invoice-status ${inv.status}`}>
                                                    {inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : ''}
                                                </span>
                                            </td>
                                            <td>{formatAmount(inv.amount, inv.currency)}</td>
                                            <td>
                                                {inv.url && (
                                                    <a
                                                        href={inv.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="billing-invoice-link"
                                                    >
                                                        <span>↗</span> View
                                                    </a>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="billing-invoices-empty">No invoices yet.</p>
                        )}
                    </div>
                )}

                {/* ── Cancel ── */}
                {hasSubscription && !isCanceling && (
                    <div className="billing-cancel-row">
                        <div className="billing-section-info">
                            <h3 className="billing-section-title">Cancel</h3>
                            <p className="billing-section-desc">We'll be sad to see you go.</p>
                        </div>
                        <div className="billing-section-action">
                            <button className="billing-btn cancel" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Plan Selection Modal ── */}
            {showPlanModal && (
                <div className="billing-modal-overlay" onClick={() => setShowPlanModal(false)}>
                    <div className="billing-modal" onClick={e => e.stopPropagation()}>
                        <button className="billing-modal-close" onClick={() => setShowPlanModal(false)}>×</button>
                        <h2 className="billing-modal-title">
                            {currentPlan === 'free' ? 'Choose a plan' : 'Adjust your plan'}
                        </h2>
                        <p className="billing-modal-subtitle">Current plan: {currentPlanData.label}</p>
                        <div className="billing-plans-grid">
                            {PLANS.filter(p => p.id !== 'free').map(plan => {
                                const planIndex = PLANS.findIndex(p => p.id === plan.id);
                                const isCurrent = plan.id === currentPlan;
                                const isPending = plan.id === pendingPlan;
                                const isUpgrade = planIndex > currentPlanIndex;

                                let btnLabel = isUpgrade ? 'Upgrade' : 'Downgrade';
                                let btnClass = isUpgrade ? 'upgrade' : 'downgrade';
                                if (isCurrent) { btnLabel = 'Current plan'; btnClass = 'current'; }
                                if (isPending) { btnLabel = 'Pending downgrade'; btnClass = 'current'; }

                                return (
                                    <div key={plan.id} className={`billing-plan-card ${isCurrent ? 'current' : ''}`}>
                                        {isCurrent && <div className="billing-plan-card-badge">Current plan</div>}
                                        {isPending && <div className="billing-plan-card-badge pending">Pending</div>}
                                        <div className="billing-plan-card-body">
                                            <h4>{plan.label}</h4>
                                            <div className="billing-plan-card-price-row">
                                                <span className="billing-plan-card-price">
                                                    ${plan.price.toLocaleString()}
                                                </span>
                                                <span className="billing-plan-card-period">/mo.</span>
                                            </div>
                                            <p className="billing-plan-card-desc">
                                                ${Math.round(plan.price / plan.maxClients)} per workspace
                                            </p>
                                            <button
                                                className={`billing-plan-card-btn ${btnClass}`}
                                                onClick={() => handleSelectPlan(plan.id)}
                                                disabled={isCurrent || isPending || loading}
                                            >
                                                {btnLabel}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SubscriptionSettings;
